import clsx from 'clsx';
import { addDays, addMonths } from 'date-fns';
import ar from 'date-fns/locale/ar';
import { useTranslation } from 'next-i18next';
import { ReactElement } from 'react';
import * as React from 'react';
import DatePicker, { registerLocale } from 'react-datepicker';

import 'react-datepicker/dist/react-datepicker.min.css';

import { Typography } from '@/shared/components/atoms';
import {
  addDayHandler,
  addMonthHandler,
  datePickerChangeHandler,
  getDateFormat,
  getDayNameFormat,
  getDayNumberFormat,
  isEqualCurrenSelectedtDay,
  isExceedCurrentDay,
  subDayHandler,
  subMonthHandler,
} from '@/shared/components/molecules/CustomDatePicker/logic';
import { screens } from '@/shared/constant/screens';
import useMatchMedia from '@/shared/hooks/useMatchMedia';

import Calender from '~/images/shared/calender.svg';
import LeftArrow from '~/images/shared/leftArrow.svg';
registerLocale('ar', ar);

interface IDatePickerWithDaysProps {
  defaultDate?: Date;
  onChange: (date: Date) => void;
}
/**
 *
 * @defaultDate  {Date} : default selected day if not passed that will be current day
 * @onChange   : {fun(Date)} : when change selected day will call this fun with the new selected day
 * @returns
 */
export default function DatePickerWithDays({
  defaultDate = new Date(),
  onChange,
}: IDatePickerWithDaysProps): ReactElement {
  const [date, setDate] = React.useState(defaultDate);
  const [selectedDate, setSelectedDate] = React.useState(defaultDate);
  const isMountRef = React.useRef(false);
  const isDesktopScreen = useMatchMedia(`(min-width: ${screens.desktop})`);
  const isLabtopScreen = useMatchMedia(`(min-width: ${screens.laptop})`);

  // responsive number of days card depend on screen size
  const numberOfDays = isDesktopScreen ? 14 : isLabtopScreen ? 7 : 3;

  const {
    i18n: { language },
  } = useTranslation();

  React.useEffect(() => {
    // to scape call onChange in first mount
    if (isMountRef.current) onChange(selectedDate);
    isMountRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  /* **************************************** datepicker with days ******************************************* */

  return (
    <div className='flex flex-col rounded-lg border-xs border-background-100 shadow-100'>
      {/* ******************* datePicker ******************** */}
      <div className='flex   h-82 items-center justify-center gap-2 bg-white'>
        {/* decrease (Month) */}
        <span
          onClick={() => subMonthHandler(date, setDate)}
          className='cursor-pointer rounded-full bg-primary-default p-6  text-14 text-white rtl:rotate-180'
        >
          <LeftArrow />
        </span>
        <span>
          <DatePicker
            wrapperClassName='relative '
            calendarClassName='capitalize'
            showMonthYearPicker
            maxDate={new Date()}
            locale={language}
            selected={date}
            onChange={(pickerDate: Date) =>
              datePickerChangeHandler(pickerDate, setDate)
            }
            customInput={
              <Typography
                variant='subtitle2'
                weight='semiBold'
                className='flex cursor-pointer items-center gap-2 capitalize text-primary-default'
              >
                <Calender /> {getDateFormat(date, language, 'MMMM-YYY')}
              </Typography>
            }
          />
        </span>
        {/* increase (Month) */}
        <button
          disabled={isExceedCurrentDay(addMonths(date, 1))}
          onClick={() => addMonthHandler(date, setDate)}
          className='cursor-pointer rounded-full bg-primary-default p-6 text-14 text-white disabled:pointer-events-none disabled:bg-background-disable ltr:rotate-180'
        >
          <LeftArrow />
        </button>
      </div>

      {/* **************************** week days *************************** */}

      <div className='flex h-82 items-center justify-between bg-primary-100 px-20'>
        {/* decrease (Day) */}
        <span
          onClick={() => subDayHandler(date, setDate)}
          className=' cursor-pointer rounded-full border-xs border-primary-default bg-white p-6 text-14  text-primary-default  rtl:rotate-180'
        >
          <LeftArrow />
        </span>
        {/* **************** Days card ***************** */}
        {Array(numberOfDays)
          .fill(0)
          .map((_, i) => (
            <button
              key={i}
              disabled={isExceedCurrentDay(addDays(date, i))}
              onClick={() => setSelectedDate(addDays(date, i))}
              className='disabled:pointer-events-none disabled:opacity-5'
            >
              <Typography
                variant='subtitle1'
                weight='semiBold'
                className={clsx(
                  'flex h-64 min-w-[4.5rem] cursor-pointer flex-col items-center justify-center  rounded-lg capitalize ',
                  isEqualCurrenSelectedtDay(selectedDate, addDays(date, i))
                    ? 'bg-primary-default text-white hover:bg-primary-default '
                    : 'text-primary-default hover:bg-primary-200'
                )}
              >
                <div>{getDayNameFormat(date, i, language)} </div>
                <div>{getDayNumberFormat(date, i)} </div>
              </Typography>
            </button>
          ))}
        {/* increase (Day) */}
        <button
          disabled={isExceedCurrentDay(addDays(date, 1))}
          onClick={() => addDayHandler(date, setDate)}
          className=' cursor-pointer rounded-full border-xs border-primary-default bg-white p-6 text-14 text-primary-default disabled:pointer-events-none  disabled:text-background-disable ltr:rotate-180'
        >
          <LeftArrow />
        </button>
      </div>
    </div>
  );
}
